import { Injectable } from '@angular/core';
import { RecordingSettings, RecordingFlagDTO, DispositionDTO, RecordingWTO, GroupLists, ManualRecordingSettings, MonitorData, SecurityData, View } from 'src/app/recordings/recordings.model';
import { CVApiService } from 'src/app/reusables/services/cv-api.service';
import { AgGridHelperService } from 'src/app/reusables/services/ag-grid-helper.service';
import { GridOptions, RowNode } from 'ag-grid-community';
import { formatDate } from '@angular/common';
import { WallboardTextTruncateRenderer } from 'src/app/wallboards/wallboards.model';

@Injectable({
  providedIn: 'root'
})
export class RecordingsMainService {
    public view: View = View.Landing;

    public localization: Map<string, string>;
    public gridOptions: GridOptions = null;
    public cvAngularDateTimeFormat: string = window["CVAngularDateTimeFormat"];

    public recordings: RecordingWTO[] = [];
    public recordingForDetails: RecordingWTO = null;
    public groupLists: GroupLists;
    public accessData: SecurityData = { MonitorRecordingAccess: false, CalibrationRecordingAccess: false, MonitorableStatusAccess: false };
    public flags: RecordingFlagDTO[];

    get IndexForDetails(): number {
        return this.recordings.findIndex(o => o.RecordingID == this.recordingForDetails.RecordingID);
    }

    // These are here so that the landing view's cv-loader can show in response to the details view pulling information
    public isUrlLoading: boolean = false;
    public areFlagsLoading: boolean = false;
    public isMonitoringDataLoading: boolean = false;

    constructor(
        private cvApiService: CVApiService,
        private agGridHelperService: AgGridHelperService,
    ) { }

    public async loadRecordings(settings: RecordingSettings, manual = false) {
        let correctedSettings = angular.copy(settings);

        // Dispositions are identified by their names, and this causes a problem when those names can be different based on localization.
        if (correctedSettings.SelectedDisposition == this.localization.get("All"))
            correctedSettings.SelectedDisposition = "All";

        try {
            let response = await this.cvApiService.post<RecordingWTO[]>(`/api/v1/recordings?utcOffset=${settings.StartDate.getTimezoneOffset()}&manual=${manual}`, JSON.stringify(correctedSettings));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error('Failed to load recordings');
        }
    }

    public async loadAllDispositions() {
        try {
            let response = await this.cvApiService.get<DispositionDTO[]>(`/api/v1/recordings/dispositions`);
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error('Failed to load dispositions');
        }
    }

    public async loadAllFlags() {
        try {
            let response = await this.cvApiService.get<RecordingFlagDTO[]>(`/api/v1/recordings/allFlags`);
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error('Failed to load flags');
        }
    }

    public async addManualRecording(settings: ManualRecordingSettings) {
        try {
            let response = await this.cvApiService.post<number>(`/api/v1/recordings/manual?utcOffset=${settings.ContactDate.getTimezoneOffset()}`, JSON.stringify(settings));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error('Failed to add manual record');
        }
    }

    public async loadUrl(recordingId: number) {
        try {
            let response = await this.cvApiService.get<string>(`/api/v1/recordings/url?recordingId=${recordingId}`);
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error('Failed to load URL');
            return "";
        }
    }

    public async loadMonitoringData(recordingId: number) {
        try {
            let response = await this.cvApiService.get<MonitorData[]>(`/api/v1/recordings/monitored?recordingId=${recordingId}`);
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error('Failed to load monitoring data');
            return [];
        }
    }

    public async editOtherId(recordingId: number, otherId: string) {
        try {
            let response = await this.cvApiService.put(`/api/v1/recordings/otherId?recordingId=${recordingId}`, JSON.stringify(otherId));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error('Failed to update Other ID');
            throw error;
        }
    }

    public async loadFlags(recordingId: number) {
        try {
            let response = await this.cvApiService.get<RecordingFlagDTO[]>(`/api/v1/recordings/flags?recordingId=${recordingId}`);
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error('Failed to load flag names');
            //this.recordingDetailsFlagNamesAssigned = 'Failed to load flag names.';
        }
    }

    public async editFlags(recordingId: number, flags: RecordingFlagDTO[]) {
        try {
            let response = await this.cvApiService.put(`/api/v1/recordings/flags?recordingId=${recordingId}`, JSON.stringify(flags));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error('Failed to update flags');
            throw error;
        }
    }

    public async loadDetailSecurity() {
        try {
            let response = await this.cvApiService.get<SecurityData>(`/api/v1/recordings/securityAccess`);
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error('Failed to load security values');
            return { MonitorRecordingAccess: false, CalibrationRecordingAccess: false, MonitorableStatusAccess: false };
        }
    }

    public async loadGroupLists() {
        try {
            let response = await this.cvApiService.get<GroupLists>(`/api/v1/recordings/groupLists`);
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error('Failed to load group lists');
        }
    }

    public async editAllowMonitoring(recordingId: number, flagValue: boolean) {
        try {
            let response = await this.cvApiService.put(`/api/v1/recordings/allowMonitoring?recordingId=${recordingId}`, JSON.stringify(flagValue));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error('Failed to toggle allow monitoring');
            return [];
        }
    }

    /**
     * Changes to the specified view on the page. 
     * @param view 0 = Landing, 1 = Result List, 2 = Details
     * @param recording The recording to display details for. Required if view parameter is 2, does nothing if view is set to 0 or 1.
     */
    public changeView(view: View, recording: RecordingWTO = null): void {
        if (view < 0 || view > 2)
            return;

        if (view == View.ResultList) {
            this.gridOptions = this.agGridHelperService.createGridOptions({
                columnDefs: [
                    {
                        field: "ContactID",
                        headerName: this.localization.get("Contact_ID"),
                        onCellClicked: event => this.changeView(2, event.data),
                        cellClass: "d-flex align-items-center color-cerulean clickable",
                        cellRenderer: WallboardTextTruncateRenderer
                    },
                    {
                        field: "RecordingType",
                        headerName: this.localization.get("Type"),
                        valueGetter: (params) => this.formatRecordingType(params.data.RecordingType),
                        cellRenderer: WallboardTextTruncateRenderer
                    },
                    {
                        field: "EmpGroup1ID",
                        headerName: this.groupLists.Emp1Label,
                        valueGetter: (params) => params.data.EmpGroup1ID > 0 ? params.data.EmpGroup1Name : "Unknown group value",
                        cellRenderer: WallboardTextTruncateRenderer
                    },
                    {
                        field: "RecGroup1ID",
                        headerName: this.groupLists.RecLevels.length > 0 ? this.groupLists.RecLevels[0].GroupTypeName : "RecGroup",
                        valueGetter: (params) => params.data.RecGroup1ID > 0 ? params.data.RecGroup1Name : "Unassigned",
                        cellRenderer: WallboardTextTruncateRenderer
                    },
                    {
                        field: "RecordingDate_UTC",
                        headerName: this.localization.get("Date"),
                        valueGetter: params => params.data[params.colDef.field] != null ? formatDate(params.data[params.colDef.field], this.cvAngularDateTimeFormat, "en-US") : null,
                        cellRenderer: WallboardTextTruncateRenderer,
                        comparator: (valueA, valueB, nodeA: RowNode, nodeB: RowNode) => new Date(nodeA.data.RecordingDate_UTC ?? 8640000000000000).compareTo(new Date(nodeB.data.RecordingDate_UTC ?? 8640000000000000))
                    },
                    {
                        field: "Disposition",
                        headerName: this.localization.get("Disposition"),
                        cellRenderer: WallboardTextTruncateRenderer
                    },

                    {
                        field: "ContactSource",
                        headerName: this.localization.get("Contact_Source"),
                        cellRenderer: WallboardTextTruncateRenderer
                    },
                    {
                        field: "Length",
                        headerName: this.localization.get("Length"),
                        cellRenderer: WallboardTextTruncateRenderer
                    }
                ],
                defaultColDef: {
                    sortable: true,
                    cellClass: "d-flex align-items-center",
                    menuTabs: [],
                    resizable: true
                },
                getRowId: ({ data: rowData }) => rowData.RecordingID,
                onGridSizeChanged: gridEvent => {
                    if (gridEvent.clientWidth > 0)
                        gridEvent.api.sizeColumnsToFit();
                },
                rowData: this.recordings,
                onColumnVisible: event => event.api.sizeColumnsToFit(),
                suppressRowClickSelection: true,
                suppressCellFocus: true,
                suppressMovableColumns: true,
                rowHeight: 42
            });
        }
        else if (view == View.Details) {
            if (!recording)
                return;

            this.recordingForDetails = recording;
        }

        this.view = view;
    }

    private formatRecordingType(type: number): string {
        switch (type) {
            case 0: return this.localization.get("Audio");
            case 1: return this.localization.get("Text");
            case 2: return this.localization.get("CoachingSession");
            case 3: return this.localization.get("Survey");
            case 4: return this.localization.get("Quiz");
            default: return "";
        }
    }

    /**
     * Returns true if inputted string value is not a number, false if otherwise.
     * @param value String value
     */
    public isNotANumber(value: string): boolean {
        return isNaN(Number(value));
    }

    /**
     * Returns true if keypress event was for a number key, false if otherwise.
     * @param event Keypress event
     */
    public numberOnly(event): boolean {
        const charCode = event.which ?? event.keyCode;
        if (charCode > 31 && (charCode < 48 || charCode > 57))
            return false;
        return true;
    }

    /**
     * Returns true if paste event only contained numbers, false if otherwise.
     * @param event Paste event
     */
    public pasteNumberOnly(event): boolean {
        return !this.isNotANumber(event.clipboardData.getData('text'));
    }
}
